import Close from 'apollo-react-icons/Close';
import Upload from 'apollo-react-icons/Upload';
import Button from 'apollo-react/components/Button';
import Card from 'apollo-react/components/Card';
import CardContent from 'apollo-react/components/CardContent';
import CardHeader from 'apollo-react/components/CardHeader';
import Container from 'apollo-react/components/Container';
import Divider from 'apollo-react/components/Divider';
import FileUpload from 'apollo-react/components/FileUpload';
import Grid from 'apollo-react/components/Grid';
import TextField from 'apollo-react/components/TextField';
import Typography from 'apollo-react/components/Typography';
import { unionWith } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import ConfirmDialog from '../common/ConfirmDialog';
import { API_URL, OBJ_ROLE, SYS_ROLE } from '../common/Constants';
import DetailDialog from '../common/DetailDialog';
import Dialog from '../common/Dialog';
import Loading from '../common/Loading';
import { checkFileName, getFuncroleFlg, postApi, setMessage, setURL, redirectToLogin, getBlockedExtensions, isBlockedExtension, CustomApiError } from '../common/Utils';
import './UploadPage.scss';
// スピナー表示　改善
//import { CircularProgress } from '@mui/material';

/**
 * アップロード
 */
function UploadPage() {
	const location = useLocation();
	const [t, i18n] = useTranslation();
	const navigate = useNavigate();
	const [isLoading, setIsLoading] = useState(true);
	const DIALOG_STATUS = {
		NO_DIALOG: '0',
		OK_DIALOG: '1',
		DETAIL_DIALOG: '2',
		UPLOAD_CONFIRM_DIALOG: '3',
		UPLOAD_COMPLETE_DIALOG: '4',
	};
	const [dialogStatus, setDialogStatus] = useState(DIALOG_STATUS.NO_DIALOG);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [dialogContent, setDialogContent] = useState('');
	const [detailContent, setDetailContent] = useState('');
	const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
	const [confirmDialogContent, setConfirmDialogContent] = useState('');

	// ファイル選択の権限フラグ
	const [uploadFileSelectorAuthFlg, setUploadFileSelectorAuthFlg] = useState(false);
	// お知らせの権限フラグ
	const [informationAuthFlg, setInformationAuthFlg] = useState(false);
	// 特記事項の権限フラグ
	const [noticesAuthFlg, setNoticesAuthFlg] = useState(false);
	// アップロードボタンの権限フラグ
	const [uploadFileBtnAuthFlg, setUploadFileBtnAuthFlg] = useState(false);
	// 画面に表示されている情報
	const [formValues, setFormValues] = useState({
		orgCode: '',
		orgName: '',
		serviceCode: '',
		serviceName: '',
		projCode: '',
		projName: '',
		selectedFiles: [],
		notices: '',
		information: ''
	});
	// エラーのファイル名のリスト
	const [errFileNameMessage, setErrFileNameMessage] = useState('');

	// 新規追加: ブロックされた拡張子リスト
	const [blockedExtensions, setBlockedExtensions] = useState([]);
	const [blockedFileNames, setBlockedFileNames] = useState('');

	// スピナー表示　改善
	//const [isUploading, setIsUploading] = useState(false);
	// プログレスバー用の状態変数を追加
	const [showProgress, setShowProgress] = useState(false);
	const [progressValue, setProgressValue] = useState(0);
	const [uploadStatus, setUploadStatus] = useState('');

	/**
	 * アップロードAPIの成功時の処理
	 */
	function uploadSuccess() {
		setDialogStatus(DIALOG_STATUS.UPLOAD_COMPLETE_DIALOG);
		const message = t('message.1000600');
		setDialogOpen(true);
		setDialogContent(message);
	};

	/**
	 * アップロードAPIの失敗時の処理
	 * @param {object} error エラーオブジェクト
	 */
	function uploadError(error) {
		if (error.name === 'MaintenanceApiError') {
			navigate('/', { state: { maintenanceFlg: true, }});
		} else if (error.name === 'CustomApiError') {
			//入力テキストに禁則文字が含まれている場合、azureのインフラルールによりエラーとなる
			//ただし、エラーは403 Forbiddenとしか返って来ない（＝禁則文字によるエラーだと判別できない）ため、メッセージを変更する
			const forbidCharPat1 = /.+403 Forbidden.+/;
			const forbidCharPat2 = /.+Microsoft-Azure-Application-Gateway.+/;
			if ((error.message.match(forbidCharPat1)) && (error.message.match(forbidCharPat2))) {
				const message = t('message.1000810');
				setDialogStatus(DIALOG_STATUS.OK_DIALOG);
				setDialogOpen(true);
				setDialogContent(message);

				//認証期限切れの場合は再ログイン
				redirectToLogin();
			} else {
				const message = error.message;
				setDialogStatus(DIALOG_STATUS.OK_DIALOG);
				setDialogOpen(true);
				setDialogContent(message);

				//認証期限切れの場合は再ログイン
				redirectToLogin();
			}
		} else {
			const message = 'アップロード処理でエラーが発生しました。\n(An error occurred during the upload process.)';
			setDialogStatus(DIALOG_STATUS.DETAIL_DIALOG);
			setDialogOpen(true);
			setDialogContent(message);
			setDetailContent(error.stack);

			//認証期限切れの場合は再ログイン
			redirectToLogin();
		}

		//認証期限切れの場合は再ログイン
		redirectToLogin();
	};

	/**
	 * ファイル選択時の処理
	 * @param {object} files 選択したファイルの情報
	 */
	const handleUploadFile = (files) => {
		const selectedFiles = unionWith(
		  formValues.selectedFiles,
		  files,
		  (item1, item2) => item1.name === item2.name,
		);

		// 既存のファイル名チェック
		const errFileNameList = selectedFiles
		  .filter(file => checkFileName(file.name) === false)
		  .map(file => file.name);
	
		// 拡張子ブロックリストチェック
		const blockedFileList = selectedFiles
		.filter(file => {
			const isBlocked = isBlockedExtension(file.name, blockedExtensions);
			return isBlocked;
		})
		.map(file => file.name);

		if (errFileNameList.length > 0) {
		  setErrFileNameMessage(errFileNameList.join(', '));
		} else {
		  setErrFileNameMessage('');
		}
	
		if (blockedFileList.length > 0) {
		  setBlockedFileNames(blockedFileList.join(', '));
		} else {
		  setBlockedFileNames('');
		}
	
		setFormValues({ ...formValues, selectedFiles });
	  };
	
	/**
	 * ファイル削除時の処理
	 * @param {object} file 削除したファイルの情報
	 */
	const handleDeleteFile = (file) => {
		const selectedFiles = formValues.selectedFiles.filter((item) => item.name !== file.name);
		
		// 既存のファイル名チェック
		const errFileNameList = selectedFiles
			.filter(file => checkFileName(file.name) === false)
			.map(file => file.name);
		
		// ★ ブロックリストチェックを追加 ★
		const blockedFileList = selectedFiles
			.filter(file => isBlockedExtension(file.name, blockedExtensions))
			.map(file => file.name);
		
		if (errFileNameList.length > 0) {
			setErrFileNameMessage(errFileNameList.join(', '));
		} else {
			setErrFileNameMessage('');
		}
		
		// ★ ブロックされたファイルのエラーメッセージを設定 ★
		if (blockedFileList.length > 0) {
			setBlockedFileNames(blockedFileList.join(', '));
		} else {
			setBlockedFileNames('');
		}
		
		setFormValues({ ...formValues, selectedFiles });
	}

	/**
	 * アップロードボタン押下時の処理
	 */
	const handleUploadClick = () => {
		const message = t('message.1000500');
		setDialogStatus(DIALOG_STATUS.UPLOAD_CONFIRM_DIALOG);
		setConfirmDialogOpen(true);
		setConfirmDialogContent(message)
	}

	/**
	 * ダイアログでOKボタン押下時の処理
	 */
	const onOk = () => {
		setDialogStatus(DIALOG_STATUS.NO_DIALOG);
		setDialogOpen(false);
	};

	/**
	 * アップロード時の確認ダイアログでOKボタン押下時の処理
	 */
	const onUploadConfirmOk = async () => {
		// 確認ダイアログを閉じる
		setConfirmDialogOpen(false);
		
		// プログレスバーを表示開始（0%から開始）
		showProgressBar();
		
		try {
			// アップロード用のURLを生成
			const url = setURL(API_URL.UPLOAD_CLIENT);
			
			// FormDataオブジェクトを作成してアップロードデータを設定
			let formData = new FormData();
			
			// 基本情報をFormDataに追加
			formData.append('orgLevel0Code', formValues.orgCode);      // 企業コード
			formData.append('auditCode', formValues.serviceCode);      // サービスコード
			formData.append('projCode', formValues.projCode);          // プロジェクトコード
			formData.append('notices', formValues.notices);           // 特記事項
			formData.append('information', formValues.information);   // お知らせ情報
			
			// 選択されたファイルをすべてFormDataに追加
			for (let selectedFile of formValues.selectedFiles) {
				formData.append('file', selectedFile);
			}
			
			// プログレス付きアップロードを実行
			await uploadWithProgress(url, formData);
			
		} catch (error) {
			// エラーが発生した場合の処理
			console.error('Upload failed:', error);
			
			// プログレスバーを非表示にする
			hideProgressBar();
			
			// エラーダイアログを表示
			uploadError(error);
		}
	};

	/**
	 * 確認ダイアログでキャンセルボタン押下時の処理
	 */
	const onCancel = () => {
		setDialogStatus(DIALOG_STATUS.NO_DIALOG);
		setConfirmDialogOpen(false);
	};

	/**
	 * 共通ファイル管理遷移処理
	 */
	const commonBack = () => {
		const param = {
			service: formValues.serviceCode,
			org: formValues.orgCode,
			proj: formValues.projCode,
		};
		// ファイル管理画面へ遷移
		navigate('/filemanagement', { state: param });
	};

	/**
	 * アップロード完了ダイアログでOKボタン押下時の処理
	 */
	const onCompleteOk = () => {
		setDialogStatus(DIALOG_STATUS.NO_DIALOG);
		setDialogOpen(false);
		commonBack();
	};

	/**
	 * キャンセルボタン押下時の処理
	 */
	const handleCancelClick = () => {
		commonBack();
	}
	useEffect(() => {
		(async () => {
			if (location.state === null) {
				// ファイル管理画面へ遷移
				navigate('/filemanagement');
				return;
			}
			// ブロックされた拡張子リストを取得
			const extensions = await getBlockedExtensions();
			setBlockedExtensions(extensions);
			// ファイル選択の権限フラグを設定
			setUploadFileSelectorAuthFlg(getFuncroleFlg(SYS_ROLE.UPLOAD_PAGE, OBJ_ROLE.UPLOAD_FILE_SELECTOR));
			// 特記事項の権限フラグを設定
			setNoticesAuthFlg(getFuncroleFlg(SYS_ROLE.UPLOAD_PAGE, OBJ_ROLE.NOTICES));
			// お知らせの権限フラグを設定
			setInformationAuthFlg(getFuncroleFlg(SYS_ROLE.UPLOAD_PAGE, OBJ_ROLE.INFORMATION));
			// アップロードボタンの権限フラグを設定
			setUploadFileBtnAuthFlg(getFuncroleFlg(SYS_ROLE.UPLOAD_PAGE, OBJ_ROLE.UPLOAD_FILE_BTN));
			setFormValues({ ...formValues, ...location.state });
			setIsLoading(false);
		})();
	}, []);

	// プログレスバー表示用の関数を追加
	const showProgressBar = () => {
		setShowProgress(true);
		setProgressValue(0);
		setUploadStatus('ファイルをアップロード中...');
	};

	// プログレス更新用の関数を追加
	const updateProgress = (percentage) => {
		setProgressValue(percentage);
	};

	// プログレスバー非表示用の関数を追加
	const hideProgressBar = () => {
		setShowProgress(false);
		setProgressValue(0);
		setUploadStatus('');
	};

	// プログレス付きアップロード関数を修正
	const uploadWithProgress = (url, formData) => {
		return new Promise((resolve, reject) => {
			const xhr = new XMLHttpRequest();
			
			// アップロード進捗の監視
			xhr.upload.addEventListener('progress', (event) => {
				if (event.lengthComputable) {
					const percentComplete = (event.loaded / event.total) * 100;
					updateProgress(Math.round(percentComplete));
				}
			});
			
			xhr.addEventListener('load', () => {
				if (xhr.status === 200) {
					updateProgress(100);
					setTimeout(() => {
						hideProgressBar();
						uploadSuccess();
						resolve(xhr.response);
					}, 500);
				} else if (xhr.status === 400) {
					// ★ 修正: CustomApiError を使用 ★
					const errorMessage = xhr.responseText || `Bad Request: Status ${xhr.status}`;
					console.error('Upload error (400):', errorMessage);
					reject(new CustomApiError(errorMessage));
				} else {
					// ★ 修正: CustomApiError を使用 ★
					const errorMessage = xhr.responseText || `アップロードに失敗しました: ${xhr.status}`;
					reject(new CustomApiError(errorMessage));
				}
			});
			
			xhr.addEventListener('error', (event) => {
				console.error('Upload error:', event);
				reject(new Error('ネットワークエラーが発生しました'));
			});
			
			xhr.addEventListener('timeout', () => {
				console.error('Upload timeout');
				reject(new Error('アップロードがタイムアウトしました'));
			});
			
			// XMLHttpRequestの設定
			xhr.open('POST', url, true);
			xhr.timeout = 4 * 60 * 60 * 1000; // 4時間タイムアウト
			
			// 必要なヘッダーを設定
			xhr.setRequestHeader('Sysrole', SYS_ROLE.UPLOAD_PAGE);
			
			// withCredentialsを設定（セッション情報を含める）
			xhr.withCredentials = true;
			
			// エラーハンドリング
			try {
				xhr.send(formData);
			} catch (error) {
				reject(new Error(`送信エラー: ${error.message}`));
			}
		});
	};

	if (isLoading) {
		// ロード中の場合はローディング画面
		return (
			<Loading />
		);
	} else {
		return (
			<>
			{showProgress && (
				<div style={{
					position: 'fixed',
					top: 0,
					left: 0,
					width: '100%',
					height: '100%',
					backgroundColor: 'rgba(0, 0, 0, 0.6)',
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
					zIndex: 10000
				}}>
					<div style={{
						background: 'white',
						padding: '40px',
						borderRadius: '12px',
						textAlign: 'center',
						boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
						minWidth: '400px'
					}}>
						<Typography variant="h6" style={{ marginBottom: '20px' }}>
							{uploadStatus}
						</Typography>
						<div style={{
							width: '100%',
							height: '20px',
							backgroundColor: '#f0f0f0',
							borderRadius: '10px',
							overflow: 'hidden',
							marginBottom: '15px'
						}}>
							<div style={{
								height: '100%',
								backgroundColor: '#4CAF50',
								width: `${progressValue}%`,
								transition: 'width 0.3s ease',
								borderRadius: '10px'
							}}></div>
						</div>
						<Typography variant="body2" style={{ color: '#666' }}>
							{Math.round(progressValue)}%
						</Typography>
					</div>
				</div>
			)}
		
			<Container className="uploadPage" maxWidth="md">
				<div className="row justify-content-end">
				</div>
				<Card className="card">
					<CardHeader
						title={t('upload')}
						subheader={t('uploadScreenDescription')}
					/>
					<CardContent>
						<Grid container>
							<Grid item md={12} className="griditem">
								<TextField
									label={'1. ' + t('selectService')}
									value={formValues.serviceName}
									disabled
									style={{ width: '50%' }}
								/>
							</Grid>
							<Grid item md={12} className="griditem">
								<TextField
									label={'2. ' + t('selectOrg')}
									value={formValues.orgName}
									disabled
									style={{ width: '50%' }}
								/>
							</Grid>
							<Grid item md={12} className="griditem">
								<TextField
									label={'3. ' + t('selectProj')}
									value={formValues.projName}
									disabled
									style={{ width: '50%' }} 
								/>
							</Grid>
							<Grid item md={12} className="griditem uploadFileSelector">
								<FileUpload
									label={'4. ' + t('uploadFileSelect')}
									required
									value={formValues.selectedFiles}
									onUpload={handleUploadFile}
									onFileDelete={handleDeleteFile}
									dropAreaLabels={{
										activeText: 'アップロードするファイルをドロップしてください。',
										browseLinkText: t('dropAreaBrowseLinkText'),
										hoverText: t('dropAreaHoverText'),
										maxText: '選択可能最大数'
									}}
									disabled={!uploadFileSelectorAuthFlg}
									fullWidth
								/>
								{formValues.selectedFiles.length === 0 && (
									<Typography className='fileErrorMessage' variant='caption'>
										{setMessage(t('message.1000201'), [t('file')])}
									</Typography>
								)}
								{errFileNameMessage.length !== 0 && (
									<Typography className='fileErrorMessage' variant='caption'>
										{setMessage(t('message.1000800'), [errFileNameMessage])}
									</Typography>
								)}
								{blockedFileNames.length !== 0 && (
									<Typography className='fileErrorMessage' variant='caption'>
										{setMessage(t('message.1009000'), [blockedFileNames])}
									</Typography>
								)}
							</Grid>
						</Grid>
						<Grid item md={12} className="griditem notices">
							<TextField
								label={'5. ' + t('notice')}
								helperText={formValues.notices.length > 300 ? setMessage(t('message.1000300'), [t('notice').toLowerCase(), 300]) : t('helperTextNotice')}
								value={formValues.notices}
								onChange={(e) => setFormValues({ ...formValues, notices: e.target.value })}
								error={formValues.notices.length > 300}
								multiline
								minRows="2"
								optional
								fullWidth
								disabled={!noticesAuthFlg}
							/>
						</Grid>
						<Grid item md={12} className="griditem information">
							<TextField
								label={'6. ' + t('information')}
								helperText={formValues.information.length > 300 ? setMessage(t('message.1000300'), [t('information').toLowerCase(), 300]) : t('helperTextInformation')}
								value={formValues.information}
								onChange={(e) => setFormValues({ ...formValues, information: e.target.value })}
								error={formValues.information.length > 300}
								multiline
								minRows="2"
								optional
								fullWidth
								disabled={!informationAuthFlg}
							/>
						</Grid>
						<Divider className="divider" type="dark" />
						<Button
							className="uploadFileBtn"
							variant="primary"
							icon={Upload}
							disabled={showProgress || formValues.selectedFiles.length === 0 || errFileNameMessage.length !== 0 ||  blockedFileNames.length !== 0 || formValues.notices.length > 300 || formValues.information.length > 300}
							onClick={handleUploadClick}
							style={{ display: uploadFileBtnAuthFlg ? "inline-flex" : "none"}}
						>
							{showProgress ? 'アップロード中...' : t('upload')}
						</Button>
						<Button
							className="cancelBtn"
							variant="secondary"
							icon={Close}
							onClick={handleCancelClick}
						>
							{t('cancel')}
						</Button>
						{dialogStatus === DIALOG_STATUS.OK_DIALOG && (
							<Dialog
								isOpen={dialogOpen}
								content={dialogContent}
								onOk={onOk}
							/>
						)}
						{dialogStatus === DIALOG_STATUS.DETAIL_DIALOG && (
							<DetailDialog
								isOpen={dialogOpen}
								content={dialogContent}
								detailContent={detailContent}
								onOk={onOk}
							/>
						)}
						{dialogStatus === DIALOG_STATUS.UPLOAD_CONFIRM_DIALOG && (
							<ConfirmDialog
								isOpen={confirmDialogOpen}
								content={confirmDialogContent}
								onOk={onUploadConfirmOk}
								onCancel={onCancel}
							/>
						)}
						{dialogStatus === DIALOG_STATUS.UPLOAD_COMPLETE_DIALOG && (
							<Dialog
								isOpen={dialogOpen}
								content={dialogContent}
								onOk={onCompleteOk}
							/>
						)}
					</CardContent>
				</Card>
			</Container>
		</>	
		);
	}
}

export default UploadPage;
